import React from 'react';
import { connect } from 'react-redux';
import {
  Box, Snackbar, Alert, Typography, Chip,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Tooltip,
} from '@mui/material';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

import AlertDialog from '../../../components/AlertDialog/index.es6';
import { SmsTemplateCreateUpdate } from '../../../components/smsTemplateCreateUpdate/index.es6';

import { hasAuthourity } from '../../../lib/access.es6';
import { formatDateLocale } from '../../../lib/utils.es6';

import {
  getOrgSmsTemplatesRequest,
  createOrgSmsTemplateRequest,
  updateOrgSmsTemplateRequest,
  deleteOrgSmsTemplateRequest,
  resetCreateOrgSmsTemplateSuccess,
  resetCreateOrgSmsTemplateError,
  resetUpdateOrgSmsTemplateSuccess,
  resetUpdateOrgSmsTemplateError,
  resetDeleteOrgSmsTemplateSuccess,
  resetDeleteOrgSmsTemplateError,
} from '../../OrganizationSmsTemplates/Redux/actions';

var TH_STYLE = {
  fontSize: 11, fontWeight: 700, color: '#6B7280',
  textTransform: 'uppercase', letterSpacing: '0.05em',
  bgcolor: '#F9FAFB', borderBottom: '1px solid #E5E7EB',
  py: 1.25, px: 2,
};

var TD_STYLE = {
  fontSize: 13, color: '#374151',
  borderBottom: '1px solid #F3F4F6',
  py: 1.5, px: 2,
};

function flagLabel(card) {
  if (card.system_generated) return 'System Generated';
  if (card.flash) return 'Flash';
  if (card.reply_to) return 'Reply-To';
  return null;
}

class SmsTemplatesTab extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      openUpdateDialog: false,
      smsTemplateToUpdate: null,
      showDeleteConfirm: false,
      templateToDelete: null,
      snackbarOpen: false,
      snackbarMessage: '',
      snackbarSeverity: 'success',
    };
  }

  componentWillMount() {
    this.props.dispatch(
      getOrgSmsTemplatesRequest(this.props.organizationGuid)
    );
  }

  componentDidUpdate(prevProps) {
    var d = this.props.orgSmsTemplates;
    var p = prevProps.orgSmsTemplates;
    var orgGuid = this.props.organizationGuid;

    if (d.created && !p.created) {
      this.openSnack('SMS template created successfully', 'success');
      this.props.dispatch(resetCreateOrgSmsTemplateSuccess());
      this.props.dispatch(getOrgSmsTemplatesRequest(orgGuid));
    }
    if (d.createError && !p.createError) {
      this.openSnack('Create failed: ' + String(d.createError), 'error');
      this.props.dispatch(resetCreateOrgSmsTemplateError());
    }
    if (d.updated && !p.updated) {
      this.setState({ openUpdateDialog: false, smsTemplateToUpdate: null });
      this.openSnack('SMS template updated', 'success');
      this.props.dispatch(resetUpdateOrgSmsTemplateSuccess());
      this.props.dispatch(getOrgSmsTemplatesRequest(orgGuid));
    }
    if (d.updateError && !p.updateError) {
      this.openSnack('Update failed: ' + String(d.updateError), 'error');
      this.props.dispatch(resetUpdateOrgSmsTemplateError());
    }
    if (d.deleted && !p.deleted) {
      this.setState({ showDeleteConfirm: false, templateToDelete: null });
      this.openSnack('SMS template deleted', 'success');
      this.props.dispatch(resetDeleteOrgSmsTemplateSuccess());
      this.props.dispatch(getOrgSmsTemplatesRequest(orgGuid));
    }
    if (d.deleteError && !p.deleteError) {
      this.openSnack('Delete failed: ' + String(d.deleteError), 'error');
      this.props.dispatch(resetDeleteOrgSmsTemplateError());
    }
  }

  openSnack(message, severity) {
    this.setState({
      snackbarOpen: true,
      snackbarMessage: message,
      snackbarSeverity: severity,
    });
  }

  handleCreate(formPayload) {
    var template_data = Object.assign({}, formPayload);
    delete template_data.partner_guid;
    delete template_data.guid;
    this.props.dispatch(createOrgSmsTemplateRequest({
      organization_guid: this.props.organizationGuid,
      code: formPayload.code,
      template_data: template_data,
      active: formPayload.active !== false,
    }));
  }

  openEdit(template) {
    this.setState({ openUpdateDialog: true, smsTemplateToUpdate: template });
  }

  closeEdit() {
    this.setState({ openUpdateDialog: false, smsTemplateToUpdate: null });
  }

  handleUpdate(formPayload) {
    var tmpl = this.state.smsTemplateToUpdate;
    if (!tmpl) return;
    var template_data = Object.assign({}, formPayload);
    delete template_data.partner_guid;
    delete template_data.guid;
    this.props.dispatch(updateOrgSmsTemplateRequest({
      guid: tmpl.guid,
      organization_guid: this.props.organizationGuid,
      code: formPayload.code,
      template_data: template_data,
      active: formPayload.active !== false,
    }));
  }

  confirmDelete(template) {
    this.setState({ showDeleteConfirm: true, templateToDelete: template });
  }

  cancelDelete() {
    this.setState({ showDeleteConfirm: false, templateToDelete: null });
  }

  handleDelete() {
    if (!this.state.templateToDelete) return;
    this.props.dispatch(deleteOrgSmsTemplateRequest({
      guid: this.state.templateToDelete.guid,
      organization_guid: this.props.organizationGuid,
    }));
  }

  render() {
    var d = this.props.orgSmsTemplates;
    var state = this.state;
    var list = d.list || [];

    var cards = list.map(function(t) {
      var td = t.template_data || {};
      return Object.assign({}, td, {
        guid: t.guid,
        code: t.code,
        active: t.active,
        created_at: t.created_at,
        updated_at: t.updated_at,
      });
    });

    return (
      <Box>
        {/* Header */}
        <Box sx={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'flex-start', mb: 3,
        }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 16, color: '#111827' }}>
              SMS Templates
            </Typography>
            <Typography sx={{ fontSize: 13, color: '#6B7280', mt: 0.25 }}>
              Org-scoped SMS templates sent to customers.
            </Typography>
          </Box>
          <SmsTemplateCreateUpdate
            type="create"
            supportedParameters={[]}
            createSmsTemplate={this.handleCreate.bind(this)}
            globalData={this.props.globalData}
          />
        </Box>

        {/* Inline edit form */}
        {state.openUpdateDialog && state.smsTemplateToUpdate && (
          <Box sx={{ mb: 3 }}>
            <SmsTemplateCreateUpdate
              type="update"
              supportedParameters={[]}
              updateSmsTemplate={this.handleUpdate.bind(this)}
              smsTemplateToUpdate={state.smsTemplateToUpdate}
              closeDialogUpdateSmsTemplate={this.closeEdit.bind(this)}
              globalData={this.props.globalData}
            />
          </Box>
        )}

        {/* Table */}
        <TableContainer sx={{
          border: '1px solid #E5E7EB', borderRadius: 2, overflow: 'hidden',
        }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={TH_STYLE}>Template</TableCell>
                <TableCell sx={TH_STYLE}>Code</TableCell>
                <TableCell sx={TH_STYLE}>Language</TableCell>
                <TableCell sx={TH_STYLE}>Date</TableCell>
                <TableCell sx={TH_STYLE}>Flag</TableCell>
                <TableCell sx={Object.assign({}, TH_STYLE, { textAlign: 'right' })}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {d.loading && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4, color: '#9CA3AF' }}>
                    Loading…
                  </TableCell>
                </TableRow>
              )}
              {!d.loading && cards.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <SmsOutlinedIcon sx={{ fontSize: 40, color: '#D1D5DB', mb: 1 }} />
                      <Typography sx={{ fontSize: 14, color: '#9CA3AF' }}>
                        No SMS templates yet
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
              {cards.map(function(card) {
                var flag = flagLabel(card);
                var label = (card.code || '').replace(/_/g, ' ');
                var lang = card.language ? card.language.toUpperCase() : '';
                return (
                  <TableRow
                    key={card.guid}
                    sx={{
                      '&:hover': { bgcolor: '#FAFAFA' },
                      '&:last-child td': { border: 0 },
                    }}
                  >
                    {/* Name chip */}
                    <TableCell sx={TD_STYLE}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={label + (lang ? ' (' + lang + ')' : '')}
                          size="small"
                          sx={{
                            height: 22, fontSize: 11, fontWeight: 600,
                            bgcolor: '#FFF7ED', color: '#C2410C',
                            border: '1px solid #FED7AA',
                            maxWidth: 240,
                          }}
                        />
                        {!card.active && (
                          <Chip label="Inactive" size="small" sx={{
                            height: 18, fontSize: 10, fontWeight: 600,
                            bgcolor: '#FEF2F2', color: '#B91C1C',
                            border: '1px solid #FECACA',
                          }} />
                        )}
                      </Box>
                    </TableCell>

                    {/* Code */}
                    <TableCell sx={TD_STYLE}>
                      <Typography sx={{ fontSize: 12, fontFamily: 'monospace', color: '#6B7280' }}>
                        {card.code || '—'}
                      </Typography>
                    </TableCell>

                    {/* Language */}
                    <TableCell sx={TD_STYLE}>
                      {lang ? (
                        <Chip label={lang} size="small" sx={{
                          height: 20, fontSize: 10, fontWeight: 700,
                          bgcolor: '#EEF2FF', color: '#3730A3',
                          border: '1px solid #C7D2FE',
                        }} />
                      ) : <span style={{ color: '#D1D5DB' }}>—</span>}
                    </TableCell>

                    {/* Date */}
                    <TableCell sx={TD_STYLE}>
                      <Typography sx={{ fontSize: 12, color: '#6B7280' }}>
                        {card.created_at ? formatDateLocale(card.created_at) : '—'}
                      </Typography>
                    </TableCell>

                    {/* Flag */}
                    <TableCell sx={TD_STYLE}>
                      {flag ? (
                        <Chip label={flag} size="small" sx={{
                          height: 20, fontSize: 10, fontWeight: 500,
                          bgcolor: '#F0F9FF', color: '#0369A1',
                          border: '1px solid #BAE6FD',
                        }} />
                      ) : <span style={{ color: '#D1D5DB' }}>—</span>}
                    </TableCell>

                    {/* Actions */}
                    <TableCell sx={Object.assign({}, TD_STYLE, { textAlign: 'right' })}>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        {hasAuthourity('UPDATE_SMS_TEMPLATE') && (
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={function() { this.openEdit(card); }.bind(this)}
                              sx={{ color: '#0D47A1', p: 0.75 }}
                            >
                              <EditOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasAuthourity('DELETE_SMS_TEMPLATE') && (
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={function() { this.confirmDelete(card); }.bind(this)}
                              sx={{ color: '#B91C1C', p: 0.75 }}
                            >
                              <DeleteOutlineOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              }.bind(this))}
            </TableBody>
          </Table>
        </TableContainer>

        {state.showDeleteConfirm && (
          <AlertDialog
            show
            title="Delete SMS Template"
            type="warning"
            showConfirm
            showCancel
            confirmBtnText="Yes, Delete"
            cancelBtnText="Cancel"
            reverseConfirmAndCancelButtons
            onConfirm={this.handleDelete.bind(this)}
            onCancel={this.cancelDelete.bind(this)}
          >
            <div style={{ fontSize: 14 }}>
              Are you sure you want to delete{' '}
              <strong>{state.templateToDelete && state.templateToDelete.code}</strong>?
              This action cannot be undone.
            </div>
          </AlertDialog>
        )}

        <Snackbar
          open={state.snackbarOpen}
          autoHideDuration={4000}
          onClose={function() { this.setState({ snackbarOpen: false }); }.bind(this)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            severity={state.snackbarSeverity}
            onClose={function() { this.setState({ snackbarOpen: false }); }.bind(this)}
          >
            {state.snackbarMessage}
          </Alert>
        </Snackbar>
      </Box>
    );
  }
}

export default connect(function(state) {
  return {
    orgSmsTemplates: state.orgSmsTemplatesData,
    globalData: state.global,
  };
})(SmsTemplatesTab);
